import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Plus, Trash2, Pencil, Download, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const PAYMENT_TYPES = ["Cash", "Online"];

function todayISO() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function formatINR(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

export default function ExpensesPage() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [types, setTypes] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exporting, setExporting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const emptyForm = {
    amount: "",
    payment_type: "Cash",
    market: "",
    expense_type: "",
    details: "",
    date: todayISO(),
  };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const [e, t, m] = await Promise.all([
        api.get("/expenses", { params }),
        api.get("/expense-types"),
        api.get("/expense-markets"),
      ]);
      setExpenses(e.data);
      setTypes(t.data);
      setMarkets(m.data);
      setForm((f) => ({
        ...f,
        market: f.market || m.data[0]?.name || "",
        expense_type: f.expense_type || t.data[0]?.name || "",
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [startDate, endDate]);

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm, market: markets[0]?.name || "", expense_type: types[0]?.name || "" });
    setOpen(true);
  };

  const openEdit = (ex) => {
    setEditingId(ex.id);
    setForm({
      amount: String(ex.amount),
      payment_type: ex.payment_type,
      market: ex.market,
      expense_type: ex.expense_type,
      details: ex.details || "",
      date: (ex.date || "").slice(0, 10) || todayISO(),
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter a valid amount");
    if (!form.market) return toast.error("Select an expense market");
    if (!form.expense_type) return toast.error("Select an expense type");
    setSubmitting(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (editingId) {
        await api.patch(`/expenses/${editingId}`, payload);
        toast.success("Expense updated");
      } else {
        await api.post("/expenses", payload);
        toast.success("Expense added");
      }
      setForm({
        amount: "",
        payment_type: "Cash",
        market: markets[0]?.name || "",
        expense_type: types[0]?.name || "",
        details: "",
        date: todayISO(),
      });
      setEditingId(null);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save expense");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await api.get("/expenses/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `expenses_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exported CSV");
    } catch {
      toast.error("Failed to export");
    } finally {
      setExporting(false);
    }
  };

  const clearDates = () => { setStartDate(""); setEndDate(""); };
  const canModify = (ex) => user?.role === "admin" || ex.created_by === user?.id;
  const total = expenses.reduce((s, x) => s + x.amount, 0);

  return (
    <div className="p-6 sm:p-10 max-w-[1400px] mx-auto animate-fade-in-up" data-testid="expenses-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-taupe-500 mb-2">Cost log</div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tighter text-espresso-900">
            Expenses
          </h1>
          <p className="text-taupe-600 mt-2 text-sm">
            {expenses.length} entries · <span className="font-semibold text-terracotta-500">{formatINR(total)}</span> total
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={exportCsv}
            disabled={exporting || expenses.length === 0}
            variant="outline"
            className="rounded-xl border-taupe-200 hover:border-espresso-800 hover:bg-taupe-100 text-espresso-800 font-semibold h-11"
            data-testid="export-expenses-button"
          >
            <Download className="w-4 h-4 mr-2" /> {exporting ? "Exporting…" : "Export CSV"}
          </Button>

        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); } }}>
          <DialogTrigger asChild>
            <Button
              type="button"
              onClick={openAdd}
              className="rounded-xl bg-espresso-800 hover:bg-espresso-900 text-white font-semibold px-6 h-11"
              data-testid="add-expense-button"
            >
              <Plus className="w-4 h-4 mr-2" /> Add expense
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl tracking-tight text-espresso-900">{editingId ? "Edit expense" : "Add expense"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="expense-form">
              <div>
                <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Amount (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  required
                  className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
                  data-testid="expense-amount-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Payment</Label>
                  <Select value={form.payment_type} onValueChange={(v) => setForm({ ...form, payment_type: v })}>
                    <SelectTrigger className="rounded-xl h-11 bg-taupe-100 border-taupe-200" data-testid="expense-payment-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TYPES.map((p) => (
                        <SelectItem key={p} value={p} data-testid={`expense-payment-option-${p.toLowerCase()}`}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Date</Label>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
                    data-testid="expense-date-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Market</Label>
                <Select value={form.market} onValueChange={(v) => setForm({ ...form, market: v })}>
                  <SelectTrigger className="rounded-xl h-11 bg-taupe-100 border-taupe-200" data-testid="expense-market-trigger">
                    <SelectValue placeholder="Choose market" />
                  </SelectTrigger>
                  <SelectContent>
                    {markets.map((m) => (
                      <SelectItem key={m.id} value={m.name} data-testid={`expense-market-option-${m.name.toLowerCase()}`}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Expense type</Label>
                <Select value={form.expense_type} onValueChange={(v) => setForm({ ...form, expense_type: v })}>
                  <SelectTrigger className="rounded-xl h-11 bg-taupe-100 border-taupe-200" data-testid="expense-type-trigger">
                    <SelectValue placeholder="Choose type" />
                  </SelectTrigger>
                  <SelectContent>
                    {types.map((t) => (
                      <SelectItem key={t.id} value={t.name} data-testid={`expense-type-option-${t.name.toLowerCase()}`}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-semibold text-espresso-800 mb-2 block">
                  Details (e.g. &quot;1 KG&quot;, &quot;5 packets&quot;)
                </Label>
                <Textarea
                  rows={2}
                  value={form.details}
                  onChange={(e) => setForm({ ...form, details: e.target.value })}
                  placeholder="Optional quantity or notes"
                  className="rounded-xl bg-taupe-100 border-taupe-200"
                  data-testid="expense-details-input"
                />
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="rounded-xl bg-espresso-800 hover:bg-espresso-900 text-white font-semibold"
                  data-testid="submit-expense-button"
                >
                  {submitting ? "Saving…" : editingId ? "Update expense" : "Save expense"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Date range filter */}
      <div className="card-soft p-4 sm:p-5 mb-6 flex flex-wrap items-end gap-3" data-testid="expenses-filters">
        <div className="flex-1 min-w-[160px]">
          <Label className="text-xs font-semibold text-taupe-600 mb-1.5 block uppercase tracking-wider">From</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
            data-testid="expenses-start-date"
          />
        </div>
        <div className="flex-1 min-w-[160px]">
          <Label className="text-xs font-semibold text-taupe-600 mb-1.5 block uppercase tracking-wider">To</Label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
            data-testid="expenses-end-date"
          />
        </div>
        {(startDate || endDate) && (
          <Button
            type="button"
            variant="ghost"
            onClick={clearDates}
            className="rounded-xl h-11 text-taupe-600 hover:text-espresso-800"
            data-testid="clear-expenses-filter"
          >
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        )}
      </div>

      <div className="card-soft overflow-hidden" data-testid="expenses-table-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-taupe-100 text-taupe-600 text-xs font-semibold uppercase tracking-wider">
                <th className="text-left py-4 px-6">Date</th>
                <th className="text-left py-4 px-6">Type</th>
                <th className="text-left py-4 px-6">Market</th>
                <th className="text-left py-4 px-6">Payment</th>
                <th className="text-left py-4 px-6">Details</th>
                <th className="text-right py-4 px-6">Amount</th>
                <th className="text-right py-4 px-6" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="py-8 text-center text-taupe-500 text-sm">Loading…</td></tr>
              )}
              {!loading && expenses.length === 0 && (
                <tr><td colSpan={7} className="py-12 text-center text-taupe-500 text-sm" data-testid="expenses-empty">
                  No expenses yet. Log your first purchase.
                </td></tr>
              )}
              {expenses.map((e) => (
                <tr key={e.id} className="border-t border-taupe-200 hover:bg-taupe-100/40 transition-colors" data-testid="expenses-row">
                  <td className="py-4 px-6 text-sm text-espresso-900">{new Date(e.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                  <td className="py-4 px-6 text-sm font-semibold text-espresso-900">{e.expense_type}</td>
                  <td className="py-4 px-6 text-sm text-taupe-700">{e.market}</td>
                  <td className="py-4 px-6 text-sm">
                    <span className="inline-flex px-2.5 py-1 rounded-md bg-terracotta-100 text-terracotta-500 text-xs font-semibold">{e.payment_type}</span>
                  </td>
                  <td className="py-4 px-6 text-sm text-taupe-700 max-w-[240px] truncate">{e.details || <span className="text-taupe-500">—</span>}</td>
                  <td className="py-4 px-6 text-right font-semibold text-espresso-900">{formatINR(e.amount)}</td>
                  <td className="py-4 px-6 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => openEdit(e)}
                        disabled={!canModify(e)}
                        className="p-2 rounded-lg text-espresso-800 hover:bg-taupe-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`edit-expense-${e.id}`}
                        aria-label="Edit"
                        title={canModify(e) ? "Edit" : "Only owner or admin can edit"}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => remove(e.id)}
                        disabled={!canModify(e)}
                        className="p-2 rounded-lg text-terracotta-500 hover:bg-terracotta-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`delete-expense-${e.id}`}
                        aria-label="Delete"
                        title={canModify(e) ? "Delete" : "Only owner or admin can delete"}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
